import React, { useState, useEffect, useCallback } from 'react';
import PersonaTagChips from '../components/PersonaTagChips';

const CATEGORY_COLORS = {
  Templates: '#1565C0',
  Policies:  '#6A1B9A',
  Training:  '#00796B',
  Other:     '#455A64',
};

function formatBytes(b) {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }); }
  catch { return iso; }
}

export default function ToolkitLibrary({ API, persona, setPage }) {
  const [items, setItems]           = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPageNum]          = useState(1);
  const [pageSize]                  = useState(50);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // Auth + active-tenant headers are attached by the global fetch interceptor
  // installed in src/auth/msalConfig.js — no per-call header mapping required.
  const headers = useCallback(() => ({}), []);

  const load = useCallback(async (resetPage) => {
    setLoading(true);
    setError(null);
    const usePage = resetPage ? 1 : page;
    try {
      const qs = new URLSearchParams();
      if (selectedCategory) qs.set('category', selectedCategory);
      if (search.trim())    qs.set('search', search.trim());
      qs.set('page', String(usePage));
      qs.set('pageSize', String(pageSize));

      const res = await fetch(`${API}/api/v1/toolkit?${qs.toString()}`, { headers: headers() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setItems(body.items || []);
      setCategories(body.categories || []);
      setTotal(body.total || 0);
      if (resetPage) setPageNum(1);
    } catch (e) {
      setError(e.message || 'Failed to load toolkit');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [API, headers, selectedCategory, search, page, pageSize]);

  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [selectedCategory]);
  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [page]);

  const handleSearchSubmit = (e) => { e.preventDefault(); load(true); };
  const clearFilters = () => { setSelectedCategory(''); setSearch(''); };

  const download = async (item) => {
    try {
      const res = await fetch(`${API}/api/v1/toolkit/${item.id}/download`, { headers: headers() });
      if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.fileName || 'toolkit-item';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || 'Download failed');
    }
  };

  const isAdmin = persona === 'admin';

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:24,alignItems:'start'}}>
        {/* ── Filter sidebar ── */}
        <div style={{background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,padding:14,position:'sticky',top:0}}>
          <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:10}}>
            Categories
          </div>

          <CategoryRadio
            label="All"
            value=""
            current={selectedCategory}
            count={total}
            onChange={setSelectedCategory}
            color="#455A64"
          />
          {categories.map(c => (
            <CategoryRadio
              key={c.name}
              label={c.name}
              value={c.name}
              current={selectedCategory}
              count={c.count}
              onChange={setSelectedCategory}
              color={CATEGORY_COLORS[c.name] || '#455A64'}
            />
          ))}

          <div style={{height:1,background:'#EEF0F3',margin:'14px 0'}}/>

          <form onSubmit={handleSearchSubmit}>
            <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:6}}>
              Search
            </div>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Title or description"
              style={{width:'100%',padding:'7px 9px',fontSize:12,border:'1px solid #DDE1E6',borderRadius:6,outline:'none'}}
            />
            <button type="submit" className="btn-primary" style={{width:'100%',marginTop:8}}>Search</button>
          </form>

          {(selectedCategory || search) && (
            <button onClick={clearFilters} className="btn-ghost" style={{marginTop:8,width:'100%',textAlign:'center'}}>
              Clear filters
            </button>
          )}
        </div>

        {/* ── Main item grid ── */}
        <div>
          {loading && <div style={{padding:'40px 0',textAlign:'center',color:'#7A8899',fontSize:12}}>Loading…</div>}

          {error && (
            <div style={{padding:14,background:'#FEEBEE',border:'1px solid #FFCDD2',borderRadius:8,color:'#C62828',fontSize:12,marginBottom:14}}>
              {error}
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div style={{padding:'40px 0',textAlign:'center',color:'#7A8899'}}>
              <div style={{fontSize:13,marginBottom:6}}>No items in this category yet.</div>
              {isAdmin && (
                <button className="btn-ghost" onClick={() => setPage('toolkit-admin')} style={{fontSize:12}}>
                  Go to Toolkit Admin to upload →
                </button>
              )}
            </div>
          )}

          {items.length > 0 && (
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:14}}>
                {items.map(item => (
                  <ItemCard key={item.id} item={item} onDownload={() => download(item)} />
                ))}
              </div>

              {/* Pagination */}
              {total > pageSize && (
                <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:14,marginTop:20,fontSize:12,color:'#7A8899'}}>
                  <button className="btn-secondary" disabled={page <= 1} onClick={() => setPageNum(p => Math.max(1, p - 1))}>
                    ← Previous
                  </button>
                  <span>Page {page} of {Math.max(1, Math.ceil(total / pageSize))}</span>
                  <button className="btn-secondary" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPageNum(p => p + 1)}>
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryRadio({ label, value, current, count, onChange, color }) {
  const selected = current === value;
  return (
    <label
      style={{
        display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'7px 9px',borderRadius:6,marginBottom:3,cursor:'pointer',
        background: selected ? color + '14' : 'transparent',
        border: `1px solid ${selected ? color + '40' : 'transparent'}`,
        transition:'all 0.12s',
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#F8FAFC'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <input
          type="radio"
          checked={selected}
          onChange={() => onChange(value)}
          style={{accentColor: color}}
        />
        <span style={{fontSize:12,color: selected ? color : '#0D1B2A',fontWeight: selected ? 600 : 400}}>{label}</span>
      </div>
      <span style={{fontSize:10,color:'#7A8899'}}>{count}</span>
    </label>
  );
}

function ItemCard({ item, onDownload }) {
  return (
    <div style={{background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,padding:14,display:'flex',flexDirection:'column',gap:10}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8}}>
        <div style={{fontSize:13,fontWeight:600,color:'#0D1B2A',lineHeight:1.4,flex:1}}>{item.title}</div>
        <span style={{
          fontSize:9,padding:'2px 7px',borderRadius:6,whiteSpace:'nowrap',
          background:(CATEGORY_COLORS[item.category]||'#455A64')+'14',
          color: CATEGORY_COLORS[item.category]||'#455A64',
          fontWeight:500,
        }}>
          {item.category}
        </span>
      </div>

      {item.description && (
        <div style={{fontSize:11,color:'#7A8899',lineHeight:1.5,maxHeight:48,overflow:'hidden',textOverflow:'ellipsis'}}>
          {item.description}
        </div>
      )}

      <PersonaTagChips tags={item.personaTags} editable={false} size="sm" />

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:10,color:'#7A8899',marginTop:'auto',paddingTop:6,borderTop:'1px solid #F0F2F5'}}>
        <span>{formatBytes(item.sizeBytes)} · {formatDate(item.uploadedAt)}</span>
        <button className="btn-primary" style={{padding:'5px 12px',fontSize:11}} onClick={onDownload}>Download</button>
      </div>
    </div>
  );
}
